import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { BrandKitSource } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Data {
  brand_kit_source: BrandKitSource
}

export interface Variables {
  brandKitId: number
  source: {
    type: 'Website' | 'ExternalIntegration' | 'PlainText' | 'AlmanacFile' | 'SourceMaterialsMediaObject'
    id: number
  }
  name?: string
  emoji?: string
  signal?: AbortSignal
}

export const createBrandKitSource = async ({
  workspaceId,
  source,
  brandKitId,
  name,
  emoji,
}: Variables & { workspaceId: number }) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${brandKitId}/sources`, {
    method: 'POST',
    body: {
      source_id: source.id,
      source_type: source.type,
      name,
      emoji,
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<BrandKitSource, Error, Variables>, 'mutationFn'> {}

export const useCreateBrandKitSource = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<BrandKitSource, Error, Variables>(async (vars) => {
    const { name, emoji, source, brandKitId, signal } = vars

    const result = await createBrandKitSource({
      workspaceId,
      source,
      brandKitId,
      name,
      emoji,
      signal,
    })

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])

    return result.brand_kit_source
  }, options)
}
